"use client";

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { emailSchema } from "@/library/schemas";

interface EmailPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string) => void;
  /** Optional error to display (e.g. backend rejected the email). */
  initialError?: string;
}

export default function EmailPromptModal({
  isOpen,
  onClose,
  onSubmit,
  initialError,
}: EmailPromptModalProps) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  // Surface an error passed in from the parent (e.g. ReportModal onError).
  useEffect(() => {
    if (isOpen && initialError) {
      setError(initialError);
    }
  }, [isOpen, initialError]);

  // Reset when closed.
  useEffect(() => {
    if (!isOpen) {
      setEmail("");
      setError("");
    }
  }, [isOpen]);

  // Close on ESC.
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  const handleContinue = () => {
    const result = emailSchema.safeParse(email);
    if (!result.success) {
      setError(result.error.issues[0].message);
      return;
    }
    setError("");
    onSubmit(email.trim());
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface-raised rounded-2xl max-w-md w-full shadow-2xl p-6 sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <div className="flex justify-end -mt-2 -mr-2">
          <button
            onClick={onClose}
            className="text-ink-faint hover:text-ink-muted text-2xl leading-none cursor-pointer transition-colors"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="text-center mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-ink mb-2">
            Get your free AI Visibility Report
          </h2>
          <p className="text-sm text-ink-muted">
            Enter your professional email and we&apos;ll analyze your brand&apos;s
            presence across AI platforms.
          </p>
        </div>

        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError("");
          }}
          onKeyDown={(e) => e.key === "Enter" && handleContinue()}
          placeholder="Enter your professional email..."
          autoFocus
          className="w-full px-4 py-3 border-2 border-hairline-strong rounded-lg focus:outline-none focus:border-ink text-[15px] text-ink transition-colors"
        />

        {error && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400 text-center">{error}</p>
        )}

        <button
          onClick={handleContinue}
          className="w-full mt-4 px-6 py-3.5 bg-ink text-surface font-semibold text-[15px] rounded-lg hover:bg-ink/90 transition-colors cursor-pointer"
        >
          Continue
        </button>
      </div>
    </div>,
    document.body
  );
}
